import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, CalendarDays } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eyebrow } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { getEvents } from "@/lib/api";

const CATS = [
  { key: "all", label: "All" },
  { key: "workshops", label: "Workshops" },
  { key: "live-study", label: "Live Study" },
  { key: "self-study", label: "Self-Study" },
];

export default function Events() {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEvents().then((d) => { setEvents(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = active === "all" ? events : events.filter((e) => e.category === active);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>Classes & Sessions</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">Art education, taught in the open.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">Workshops, live study and self-study sessions — structured, hands-on art education you can join and learn from at every level.</p>
        </div>
      </section>

      <div className="ytca-container py-10">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c.key}
              onClick={() => setParams(c.key === "all" ? {} : { category: c.key })}
              data-testid={`events-category-filter-${c.key}`}
              className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                active === c.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-muted-foreground">Loading classes…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-12 text-muted-foreground">No classes in this category yet — check back soon.</p>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((e, i) => (
              <Reveal key={e.slug} delay={i * 0.04}>
                <Link to={`/events/${e.slug}`} data-testid={`event-card-${e.slug}`} className="group block h-full">
                  <Card className="card-lift h-full overflow-hidden rounded-2xl border-border bg-card hover:border-primary/40">
                    <div className="relative">
                      <img src={e.image} alt={e.title} className="aspect-[16/10] w-full object-cover" />
                      <Badge className="absolute left-3 top-3 rounded-full" variant="secondary">{e.category_label}</Badge>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-xl font-semibold">{e.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{e.summary}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{e.date}</span>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="font-display text-lg font-semibold">{e.is_free ? "Free" : `₹${e.price}`}</span>
                        <span className="text-xs font-medium text-primary transition-transform group-hover:translate-x-1">View →</span>
                      </div>
                    </div>
                  </Card>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
